import { ROUTE_PATHS } from '../routes/routePaths';
import { getGlobalNavigate } from '../util/globalNavigate';
import { isAccessErrorPagePath } from './accessErrorNavigation';

/** Checks if we're already on an error page to prevent infinite redirect loops. */
export function isOnErrorPage(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return (
    window.location.pathname === ROUTE_PATHS.STANDARD_ERROR ||
    isAccessErrorPagePath(window.location.pathname)
  );
}

/**
 * Legacy ColdFusion URLs (e.g. …/previewPage.cfm?eURL=…): do not hijack with global-error
 * redirect. AppLocationGuard + cfmUrlReplacer map these to SPA routes; a stray uncaught
 * error/rejection during that transition should not replace the URL with /standard-error.
 */
export function isLegacyCfmUrl(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.location.pathname.toLowerCase().includes('.cfm');
}

const PREVIOUS_URL_STORAGE_KEY = 'errorPage:previousUrl';

/** Best-effort — sessionStorage can throw in locked-down contexts (private mode, etc.). */
function rememberPreviousUrl(): void {
  try {
    sessionStorage.setItem(PREVIOUS_URL_STORAGE_KEY, window.location.href);
  } catch {
    // Not fatal — the error page's "Try again" just falls back to reloading itself.
  }
}

/** The URL the user was on right before being redirected to the error page — for "Try again". */
export function getPreviousUrlBeforeError(): string | null {
  try {
    return sessionStorage.getItem(PREVIOUS_URL_STORAGE_KEY);
  } catch {
    return null;
  }
}

interface RedirectToErrorPageOptions {
  /**
   * Forces a hard, full-page navigation instead of the default soft route change.
   * Required when the caller is a redirect-mode ErrorBoundary: its own `render()`
   * returns `null` once it has caught an error, which unmounts everything below it —
   * including the router itself — so a soft `navigate()` has nothing left to render
   * into and the page goes blank. A hard navigation reloads from scratch, sidestepping
   * that entirely. Not needed for callers outside a boundary (e.g. the window-error /
   * unhandled-rejection listeners), where the app tree stays mounted throughout.
   */
  preferHardNavigation?: boolean;
}

/**
 * Safely redirects to the generic error page, only if not already there and not
 * mid-legacy-URL-transition. `correlationId`/`message`, when provided, are carried
 * through as query params so the error page can show a support reference and the
 * specific error description.
 *
 * Prefers a soft, client-side route change via the router's own `navigate` (registered
 * by routes/index.tsx) — unlike a hard `location.replace`, this doesn't tear down the
 * page, so it can't cancel the in-flight, fire-and-forget error-log POST that was just
 * fired alongside this call. Falls back to a hard navigation when the router hasn't
 * mounted yet, or when the caller explicitly asks for one (see `preferHardNavigation`).
 */
export function redirectToErrorPage(
  correlationId?: string,
  message?: string,
  options?: RedirectToErrorPageOptions
): void {
  if (isLegacyCfmUrl()) {
    return;
  }
  if (isOnErrorPage()) {
    return;
  }

  rememberPreviousUrl();

  const query = new URLSearchParams({ globalError: 'true' });
  if (correlationId) {
    query.set('correlationId', correlationId);
  }
  if (message) {
    query.set('message', message);
  }
  const path = `${ROUTE_PATHS.STANDARD_ERROR}?${query.toString()}`;

  if (!options?.preferHardNavigation) {
    const navigate = getGlobalNavigate();
    if (navigate) {
      void navigate(path);
      return;
    }
  }

  window.location.replace(path);
}
